import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import globalStyles from "../../../global";
import { getResponsiveSize } from "../../../utils/responsive";

const Footer = ({ countryCode = "UAE" }) => {
  const { t } = useTranslation();
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const colors = {
    background: globalStyles.colors.army_green,
    text: globalStyles.colors.white,
    mutedText: "rgba(255,255,255,0.8)",
    border: "rgba(255,255,255,0.13)",
  };

  const socialIcons = {
    facebook: `${imageBaseUrl}fb.png`,
    instagram: `${imageBaseUrl}inst.png`,
    youtube: `${imageBaseUrl}youtube.png`,
    twitter: `${imageBaseUrl}tweet.png`,
  };
  const contactIcons = {
    location: `${imageBaseUrl}location.png`,
    phone: `${imageBaseUrl}phon.png`,
    mail: `${imageBaseUrl}email.png`,
  };

  const fontSizes = globalStyles.fontSizes;

  // Helper to build translation key
  const footerKey = `footer${countryCode}`;

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: colors.background,
        color: colors.text,
        fontFamily: globalStyles.fonts.primary,
        width: "100%",
        pt: `${getResponsiveSize(48)}px`,
        pb: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: "1240px",
          mx: "auto",
          width: "100%",
          px: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1.3fr 1fr 1fr 1.7fr 1.2fr",
            },
            gap: {
              xs: `${getResponsiveSize(16)}px`,
              sm: `${getResponsiveSize(32)}px`,
              md: `${getResponsiveSize(56)}px`,
            },
            alignItems: "start",
            mb: `${getResponsiveSize(32)}px`,
          }}
        >
          {/* Logo & Description */}
          <Box>
            <Box sx={{ mb: `${getResponsiveSize(16)}px` }}>
              <img
                src={`${imageBaseUrl}footerlogo.png`}
                alt={t(`${footerKey}.logoAlt`)}
                style={{
                  width: `${getResponsiveSize(200)}px`,
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: colors.mutedText,
                fontWeight: 350,
                fontFamily: globalStyles.fonts.primary,
                fontSize: fontSizes.h6,
                lineHeight: "24px",
                letterSpacing: "0px",
                maxWidth: 290,
                mt: 0,
              }}
            >
              {t(`${footerKey}.companyDesc`)}
            </Typography>
          </Box>

          {/* Services */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: `${getResponsiveSize(12)}px`,
                color: colors.text,
                fontSize: fontSizes.h5,
                letterSpacing: "0px",
              }}
            >
              {t(`${footerKey}.services.title`)}
            </Typography>
            <Box component="ul" sx={{ pl: 0, m: 0, listStyle: "none" }}>
              {Array.isArray(
                t(`${footerKey}.services.items`, { returnObjects: true })
              ) &&
                t(`${footerKey}.services.items`, { returnObjects: true }).map(
                  (service, index) => (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        fontWeight: 350,
                        fontFamily: globalStyles.fonts.primary,
                        color: colors.text,
                        fontSize: fontSizes.h6,
                        lineHeight: "28px",
                        letterSpacing: "0px",
                        mb: 0,
                        p: 0,
                      }}
                    >
                      {service}
                    </Box>
                  )
                )}
            </Box>
          </Box>

          {/* Pages */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: `${getResponsiveSize(12)}px`,
                color: colors.text,
                fontSize: fontSizes.h5,
                letterSpacing: "0px",
              }}
            >
              {t(`${footerKey}.pages.title`)}
            </Typography>
            <Box component="ul" sx={{ pl: 0, m: 0, listStyle: "none" }}>
              {Array.isArray(
                t(`${footerKey}.pages.items`, { returnObjects: true })
              ) &&
                t(`${footerKey}.pages.items`, { returnObjects: true }).map(
                  (page, index) => (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        fontWeight: 350,
                        fontFamily: globalStyles.fonts.primary,
                        color: colors.text,
                        fontSize: fontSizes.h6,
                        lineHeight: "28px",
                        letterSpacing: "0px",
                        mb: 0,
                        p: 0,
                      }}
                    >
                      <a
                        href={page.url}
                        style={{
                          color: colors.text,
                          textDecoration: "none",
                          transition: "color 0.2s",
                          fontFamily: globalStyles.fonts.primary,
                          fontWeight: 350,
                          fontSize: fontSizes.h6,
                          lineHeight: "28px",
                          letterSpacing: "0px",
                          display: "inline-block",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = "#B7F2C1";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = colors.text;
                        }}
                      >
                        {page.name}
                      </a>
                    </Box>
                  )
                )}
            </Box>
          </Box>

          {/* Contact */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: `${getResponsiveSize(12)}px`,
                color: colors.text,
                fontSize: fontSizes.h5,
                letterSpacing: "0px",
              }}
            >
              {t(`${footerKey}.contact.title`)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: `${getResponsiveSize(8)}px`,
              }}
            >
              {/* Address */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: `${getResponsiveSize(8)}px`,
                }}
              >
                <img
                  src={contactIcons.location}
                  alt="Location"
                  style={{
                    width: `${getResponsiveSize(18)}px`,
                    marginTop: `${getResponsiveSize(2)}px`,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: fontSizes.h6,
                    lineHeight: "22px",
                    letterSpacing: "0px",
                  }}
                >
                  {t(`${footerKey}.contact.address`)}
                </Typography>
              </Box>
              {/* Phone */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: `${getResponsiveSize(8)}px`,
                }}
              >
                <img
                  src={contactIcons.phone}
                  alt="Phone"
                  style={{ width: `${getResponsiveSize(18)}px` }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: fontSizes.h6,
                    lineHeight: "22px",
                    letterSpacing: "0px",
                  }}
                >
                  {t(`${footerKey}.contact.phone`)}
                </Typography>
              </Box>
              {/* Email */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: `${getResponsiveSize(8)}px`,
                }}
              >
                <img
                  src={contactIcons.mail}
                  alt="Email"
                  style={{ width: `${getResponsiveSize(18)}px` }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: fontSizes.h6,
                    lineHeight: "22px",
                    letterSpacing: "0px",
                  }}
                >
                  {t(`${footerKey}.contact.email`)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Social & Stores */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: `${getResponsiveSize(12)}px`,
                color: colors.text,
                fontSize: fontSizes.h5,
                letterSpacing: "0px",
              }}
            >
              {t(`${footerKey}.social.title`)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: `${getResponsiveSize(12)}px`,
                mb: `${getResponsiveSize(10)}px`,
              }}
            >
              {Object.entries(
                t(`${footerKey}.social.links`, { returnObjects: true }) || {}
              ).map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex" }}
                >
                  <img
                    src={socialIcons[platform]}
                    alt={platform}
                    style={{
                      width: `${getResponsiveSize(22)}px`,
                      height: `${getResponsiveSize(22)}px`,
                      display: "block",
                    }}
                  />
                </a>
              ))}
            </Box>
            <Typography
              sx={{
                fontWeight: 350,
                fontFamily: globalStyles.fonts.primary,
                color: colors.text,
                fontSize: fontSizes.h6,
                mb: `${getResponsiveSize(8)}px`,
                lineHeight: "22px",
                letterSpacing: "0px",
              }}
            >
              {t(`${footerKey}.available.title`)}
            </Typography>
            <Box sx={{ display: "flex", gap: `${getResponsiveSize(8)}px` }}>
              <img
                src={`${imageBaseUrl}playstorewT.svg`}
                alt={t(`${footerKey}.available.playStore`)}
                style={{
                  height: `${getResponsiveSize(28)}px`,
                  width: "auto",
                  borderRadius: 4,
                  background: "#fff",
                  objectFit: "contain",
                }}
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.ucleanlaundry&hl=en_IN&pli=1"
                  )
                }
              />
              <img
                src={`${imageBaseUrl}AppstorewT.svg`}
                alt={t(`${footerKey}.available.appStore`)}
                style={{
                  height: `${getResponsiveSize(28)}px`,
                  width: "auto",
                  borderRadius: 4,
                  background: "#fff",
                  objectFit: "contain",
                }}
                onClick={() =>
                  window.open(
                    "https://apps.apple.com/in/app/uclean/id1663432955"
                  )
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Divider */}
      <Box
        sx={{
          borderTop: `1px solid ${colors.border}`,
          width: "100%",
          mt: `${getResponsiveSize(16)}px`,
        }}
      />
      {/* Copyright */}
      <Box
        sx={{
          maxWidth: "1240px",
          mx: "auto",
          width: "100%",
          py: `${getResponsiveSize(12)}px`,
          display: "flex",
          justifyContent: { xs: "center", md: "flex-end" },
        }}
      >
        <Typography
          sx={{
            color: colors.mutedText,
            fontWeight: 350,
            fontFamily: globalStyles.fonts.primary,
            fontSize: fontSizes.h6,
            textAlign: { xs: "center", md: "right" },
            width: "100%",
            lineHeight: "22px",
            letterSpacing: "0px",
          }}
        >
          {t(`${footerKey}.copyright`, { year: new Date().getFullYear() })}
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
